import { useCallback, useState } from 'react'
import { NO_FILTER, type SiteFilter } from './siteFilter'

const STORAGE_KEY = 'ow.siteFilter'

// window.localStorage, not the bare global: Node 22+ exposes an experimental
// `localStorage` of its own that shadows jsdom's under test and is unavailable
// without a flag. Saying which storage we mean costs nothing and is what the
// browser resolves to anyway.
function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

/**
 * Stored shape. Sets do not survive JSON, so they travel as arrays — and
 * `sites: null` has to stay distinguishable from `sites: []`, since one means
 * "no filter" and the other means "nothing". JSON keeps null, which is the one
 * thing this format has to get right.
 */
interface StoredFilter {
  sites: string[] | null
  cameras: Record<string, string[]>
}

function isStored(v: unknown): v is StoredFilter {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false
  const o = v as Record<string, unknown>
  const sitesOk = o.sites === null || (Array.isArray(o.sites) && o.sites.every((s) => typeof s === 'string'))
  const camerasOk =
    typeof o.cameras === 'object' && o.cameras !== null && !Array.isArray(o.cameras) &&
    Object.values(o.cameras as Record<string, unknown>).every(
      (c) => Array.isArray(c) && c.every((s) => typeof s === 'string'),
    )
  return sitesOk && camerasOk
}

function load(): SiteFilter {
  // Anything unreadable is treated as no filter. Storage can be absent, cleared,
  // blocked in a private window, or hold a shape from an older build, and none
  // of those is worth showing a dispatcher an error over.
  try {
    const raw = storage()?.getItem(STORAGE_KEY)
    if (!raw) return NO_FILTER
    const parsed: unknown = JSON.parse(raw)
    if (!isStored(parsed)) return NO_FILTER
    const cameras: Record<string, ReadonlySet<string>> = {}
    for (const [site, list] of Object.entries(parsed.cameras)) cameras[site] = new Set(list)
    return { sites: parsed.sites === null ? null : new Set(parsed.sites), cameras }
  } catch {
    return NO_FILTER
  }
}

function save(f: SiteFilter) {
  try {
    const cameras: Record<string, string[]> = {}
    for (const [site, set] of Object.entries(f.cameras)) cameras[site] = [...set]
    const stored: StoredFilter = { sites: f.sites === null ? null : [...f.sites], cameras }
    storage()?.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // A filter that cannot be remembered still works for this session.
  }
}

/**
 * The dispatcher's chosen view of sites and cameras, remembered per browser.
 *
 * Per-viewer convenience, which is what CLAUDE.md scopes browser storage to. A
 * filter that followed a dispatcher between machines would have to live on the
 * server against their user, and is a different story.
 */
export function useSiteFilter() {
  const [filter, setFilter] = useState<SiteFilter>(load)

  const apply = useCallback((next: SiteFilter) => {
    setFilter(next)
    save(next)
  }, [])

  const clear = useCallback(() => apply(NO_FILTER), [apply])

  // apply, not a toggle: choosing what the next filter is belongs to the caller,
  // because changing it also has to resolve anything on screen the new filter
  // would hide — and that has to happen in the event handler, not in an effect
  // reacting to the change afterwards.
  return { filter, applyFilter: apply, clear }
}
